import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar, RefreshCw, MapPin, Clock, Users, TrendingUp,
  Zap, Route, Car, CheckCircle, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmartSchedulingDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [optimizing, setOptimizing] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase65/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const runOptimization = async () => {
    setOptimizing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase65/optimize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date: new Date().toISOString().split('T')[0] })
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Schedule optimized! Improvement: ${result.improvement}%`);
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Optimization failed');
    } finally {
      setOptimizing(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="smart-scheduling-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Smart Scheduling</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">AI-optimized visit scheduling</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={runOptimization} disabled={optimizing}>
                {optimizing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                {optimizing ? 'Optimizing...' : 'Optimize Schedule'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="staff" className="font-semibold">Staff Schedules</TabsTrigger>
            <TabsTrigger value="suggestions" className="font-semibold">Suggestions</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <StatCard title="Visits Today" value={dashboardData?.stats?.total_visits_today || 0} icon={Calendar} color="blue" />
              <StatCard title="Completed" value={dashboardData?.stats?.visits_completed || 0} icon={CheckCircle} color="green" />
              <StatCard title="Remaining" value={dashboardData?.stats?.visits_remaining || 0} icon={Clock} color="orange" />
              <StatCard title="Opt. Score" value={`${dashboardData?.stats?.optimization_score || 0}%`} icon={TrendingUp} color="purple" />
              <StatCard title="Avg Travel" value={`${dashboardData?.stats?.avg_travel_time || 0}m`} icon={Car} color="blue" />
              <StatCard title="Utilization" value={`${dashboardData?.stats?.staff_utilization || 0}%`} icon={Users} color="teal" />
            </div>

            {/* Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Scheduling Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.alerts?.map((alert, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-4 rounded-lg border ${
                      alert.type === 'warning' ? 'bg-orange-50 border-orange-200' :
                      alert.type === 'success' ? 'bg-green-50 border-green-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <p className="font-medium text-slate-900">{alert.message}</p>
                      {alert.action && (
                        <Button size="sm" variant="outline">{alert.action}</Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Schedule Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Today's Schedule Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-slate-900">{dashboardData?.schedule_summary?.total_staff || 0}</p>
                    <p className="text-sm text-slate-600">Total Staff</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-slate-900">{dashboardData?.schedule_summary?.staff_with_visits || 0}</p>
                    <p className="text-sm text-slate-600">With Visits</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-slate-900">{dashboardData?.schedule_summary?.patients_scheduled || 0}</p>
                    <p className="text-sm text-slate-600">Patients</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-600">{dashboardData?.schedule_summary?.unscheduled_visits || 0}</p>
                    <p className="text-sm text-slate-600">Unscheduled</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="staff" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Staff Schedule Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">View individual staff schedules and workload distribution</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'Sarah Johnson', discipline: 'RN', visits: 6, utilization: 85 },
                    { name: 'Mike Williams', discipline: 'Aide', visits: 8, utilization: 92 },
                    { name: 'Lisa Chen', discipline: 'Social Worker', visits: 4, utilization: 72 },
                    { name: 'James Brown', discipline: 'RN', visits: 5, utilization: 78 },
                    { name: 'Emily Davis', discipline: 'Chaplain', visits: 3, utilization: 65 }
                  ].map((staff) => (
                    <div key={staff.name} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-slate-900">{staff.name}</p>
                        <Badge variant="outline">{staff.discipline}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Visits: <strong>{staff.visits}</strong></span>
                        <span className="text-slate-600">Utilization: <strong>{staff.utilization}%</strong></span>
                      </div>
                      <div className="mt-2 bg-gray-200 rounded-full h-2">
                        <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${staff.utilization}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="suggestions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">AI Optimization Suggestions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.optimization_suggestions?.map((suggestion, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          suggestion.impact === 'high' ? 'bg-green-100' :
                          suggestion.impact === 'medium' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <Route className={`h-5 w-5 ${
                            suggestion.impact === 'high' ? 'text-green-600' :
                            suggestion.impact === 'medium' ? 'text-blue-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <p className="font-medium text-slate-900">{suggestion.suggestion}</p>
                      </div>
                      <Badge className={
                        suggestion.impact === 'high' ? 'bg-green-500' :
                        suggestion.impact === 'medium' ? 'bg-blue-500' : 'bg-gray-500'
                      }>{suggestion.impact} impact</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SmartSchedulingDashboard;
